import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Lightbulb,
  CheckSquare,
  FileText,
  Loader2,
  Check,
  MessageSquarePlus,
} from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { CompanionMessage } from "../../api/companionMessages";
import { findTakeaway, type UserTakeaway } from "../../api/userTakeaways";
import useRetryFetch from "../../hooks/useRetryFetch";
import { useSaveTakeaway } from "../../hooks/useSaveTakeaway";
import ProgressReportDialog from "../portal/myWorkspace/actionItems/ProgressReportDialog";

interface CompanionMessageCTAsProps {
  message: CompanionMessage;
  spaceId?: string;
  className?: string;
}

/**
 * Shared CTA component for companion messages.
 * Renders: "Log progress update" per related action, "Add insight", "Add action item"
 * Used in both home feed and identity space companion cards.
 */
const CompanionMessageCTAs: React.FC<CompanionMessageCTAsProps> = ({
  message,
  spaceId,
  className,
}) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const saveTakeaway = useSaveTakeaway();

  const relatedActionIds = message.metadata?.relatedActionIds || [];

  // Fetch related action items
  const { data: relatedActions = [] } = useQuery({
    queryKey: ["companion-related-actions", message._id, ...relatedActionIds],
    queryFn: async () => {
      if (relatedActionIds.length === 0) return [];
      const results = await Promise.all(
        relatedActionIds.slice(0, 5).map(async (id) => {
          try {
            const res = await findTakeaway(retryFetch, id);
            return res.status === "ITEM" && res.item ? res.item : null;
          } catch {
            return null;
          }
        })
      );
      return results.filter(
        (item): item is UserTakeaway =>
          item !== null && item.status === "pending"
      );
    },
    enabled: relatedActionIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // Progress report dialog state
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<{
    id: string;
    content: string;
  } | null>(null);

  const handleOpenProgress = (action: UserTakeaway) => {
    setSelectedAction({ id: action._id, content: action.content });
    setProgressDialogOpen(true);
  };

  return (
    <div className={cn("space-y-2", className)}>
      {/* Log progress update buttons for related action items */}
      {relatedActions.length > 0 && (
        <div className="space-y-0.5">
          {relatedActions.map((action) => (
            <button
              key={action._id}
              onClick={() => handleOpenProgress(action)}
              className="flex items-center gap-1.5 w-full text-start py-0.5 text-[11px] text-muted-foreground/60 hover:text-teal-600 transition-colors"
            >
              <FileText className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">
                {t("log_progress_on", "Log progress:")}{" "}
                <span className="italic">
                  {action.content.length > 50
                    ? action.content.substring(0, 50) + "…"
                    : action.content}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Add insight / Add action item CTAs */}
      <InlineSaveCTAs spaceId={spaceId} saveTakeaway={saveTakeaway} />

      {/* Progress Report Dialog */}
      {selectedAction && (
        <ProgressReportDialog
          open={progressDialogOpen}
          onOpenChange={setProgressDialogOpen}
          takeawayId={selectedAction.id}
          takeawayContent={selectedAction.content}
        />
      )}
    </div>
  );
};

// ─── Inline Save CTAs ("Add my thoughts" → expand → both types) ──────────────

function InlineSaveCTAs({
  spaceId,
  saveTakeaway,
}: {
  spaceId?: string;
  saveTakeaway: ReturnType<typeof useSaveTakeaway>;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  const handleSave = async (type: "insight" | "action") => {
    const trimmed = text.trim();
    if (!trimmed || isSaving) return;

    setIsSaving(true);
    await saveTakeaway(type, trimmed, {
      spaceIds: spaceId ? [spaceId] : undefined,
      onSuccess: () => {
        setText("");
        setIsSaving(false);
        setShowSaved(true);
        setTimeout(() => {
          setShowSaved(false);
          setIsOpen(false);
        }, 1200);
      },
      onError: () => {
        setIsSaving(false);
      },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      setText("");
    }
  };

  if (showSaved) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium py-1">
        <Check className="w-3.5 h-3.5" />
        {t("saved", "Saved!")}
      </div>
    );
  }

  if (!isOpen) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="h-auto px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground gap-1.5"
        onClick={() => setIsOpen(true)}
      >
        <MessageSquarePlus className="w-3.5 h-3.5" />
        {t("add_my_thoughts", "Add my thoughts")}
      </Button>
    );
  }

  return (
    <div className="space-y-2">
      <textarea
        ref={textareaRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={t("type_your_thoughts", "Type your thoughts...")}
        rows={2}
        className="w-full text-sm rounded-lg border border-border/60 bg-background/50 px-3 py-2 placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/30 resize-none"
      />
      <div className="flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => {
            setIsOpen(false);
            setText("");
          }}
        >
          {t("cancel", "Cancel")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-3 text-xs gap-1 border-amber-200 text-amber-700 hover:bg-amber-50"
          disabled={!text.trim() || isSaving}
          onClick={() => handleSave("insight")}
        >
          {isSaving ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Lightbulb className="w-3 h-3" />
          )}
          {t("save_as_insight", "Save as Insight")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-3 text-xs gap-1 border-primary/20 text-primary hover:bg-primary/5"
          disabled={!text.trim() || isSaving}
          onClick={() => handleSave("action")}
        >
          {isSaving ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <CheckSquare className="w-3 h-3" />
          )}
          {t("save_as_action", "Save as Action")}
        </Button>
      </div>
    </div>
  );
}

export default CompanionMessageCTAs;
